import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { api, fmtErr } from "@/lib/api";
import { useAdminLanguage } from "@/i18n/AdminLanguage";
import AdminKndlaArchive from "@/pages/admin/AdminKndlaArchive";
import AdminKndlaPurge from "@/pages/admin/AdminKndlaPurge";

// Manage D1 event archival/deletion in Events, away from the sales dashboard.
// The official report is the source of archived/hidden flags and historical IDs.
export default function AdminKndlaEventTools({ onChanged }) {
  const { lang } = useAdminLanguage();
  const [events, setEvents] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const words = {
    es: { heading: "Archivo y eliminación de fiestas", load: "Actualizar fiestas de D1", fail: "No se pudo cargar el archivo de fiestas desde D1" },
    en: { heading: "Party archive and deletion", load: "Refresh D1 parties", fail: "Could not load party archive from D1" },
    ru: { heading: "Архив и удаление вечеринок", load: "Обновить вечеринки D1", fail: "Не удалось загрузить архив вечеринок D1" }
  }[lang] || { heading: "Archivo y eliminación de fiestas", load: "Actualizar fiestas de D1", fail: "No se pudo cargar el archivo de fiestas desde D1" };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const {data} = await api.get("/kndla/report", {timeout: 18000});
      if (!Array.isArray(data?.events)) throw Error("Invalid D1 events response");
      setEvents(data.events);
    } catch (e) {
      setError(fmtErr(e, words.fail));
    } finally {
      setLoading(false);
    }
  }, [words.fail]);

  useEffect(() => { load(); }, [load]);
  const changed = async () => {
    await load();
    await onChanged?.();
  };

  return <section className="space-y-4" data-testid="admin-kndla-event-tools">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h3 className="font-syne text-lg font-bold text-white">{words.heading}</h3>
      <button type="button" onClick={load} disabled={loading}
        className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3 py-2 text-xs font-semibold text-white/75 disabled:opacity-40">
        <RefreshCw size={14} className={loading ? "animate-spin" : ""} />{words.load}
      </button>
    </div>
    {error && <p role="alert" className="flex items-center gap-2 rounded-xl border border-red-500/25 p-3 text-sm text-red-300"><AlertTriangle size={15}/>{error}</p>}
    {events && <>
      <AdminKndlaArchive events={events} onChanged={changed} />
      <AdminKndlaPurge events={events} onDeleted={changed} />
    </>}
  </section>;
}
